import {
    HubConnectionBuilder,
    HubConnectionState,
    LogLevel,
    type HubConnection
} from "@microsoft/signalr";

/**
 * One SignalR connection per hub per page, shared by every component that needs it.
 *
 * Each hook used to build its own connection in an effect keyed on the access token. That meant:
 *   - two ConnectHub connections per Connect page (the shell and the incoming-call overlay), and a pair
 *     opened and closed again every time the shell remounted;
 *   - a hub torn down and rebuilt (new connection id, room membership lost) on any token change;
 *   - nothing survived a component remount.
 *
 * Here the connection is reference counted: `acquire()` on mount, `release()` on unmount. The last release
 * doesn't stop it at once — a short grace period lets a remount pick the same connection (same connection
 * id) back up. The access token is read through a factory at connect/reconnect time, so a renewed token is
 * used on the next negotiate without restarting anything.
 */

export interface SharedHubSnapshot {
    state: HubConnectionState;
    connectionId: string | null;
}

interface SharedHubOptions {
    url: string;
    /** Token for the negotiate. When set, the hub waits until it returns a value before connecting. */
    token?: () => string | undefined;
    /** Stateful reconnect: a brief transport drop resumes the same connection id. */
    stateful?: boolean;
    /** How long the last release waits before stopping the connection. */
    graceMs: number;
}

type Handler = (...args: unknown[]) => void;
type Finalizer = (connection: HubConnection) => Promise<unknown> | void;

const RETRY_DELAYS = [0, 2000, 5000, 10000, 20000];

export class SharedHub {
    private connection: HubConnection | null = null;
    private refs = 0;
    private stopTimer: number | undefined;
    private retryTimer: number | undefined;
    private attempt = 0;
    private starting = false;
    private readonly handlers = new Map<string, Set<Handler>>();
    private readonly attached = new WeakMap<HubConnection, Set<string>>();
    private readonly stateListeners = new Set<() => void>();
    private readonly finalizers = new Set<Finalizer>();
    snapshot: SharedHubSnapshot = { state: HubConnectionState.Disconnected, connectionId: null };

    constructor(private readonly opts: SharedHubOptions) {}

    /** The live connection, if any (for callers that need its promise-returning `invoke`). */
    current(): HubConnection | null {
        return this.connection;
    }

    acquire(): void {
        this.refs += 1;
        if (this.stopTimer !== undefined) {
            window.clearTimeout(this.stopTimer);
            this.stopTimer = undefined;
        }
        // Whoever released last is being replaced by this holder (a remount): its "on final release"
        // work, like leaving the meeting, must not run under the new holder.
        this.finalizers.clear();
        if (!this.connection) this.build();
        void this.ensureStarted();
    }

    /** `finalizer` runs just before the connection is actually stopped (not if it is re-acquired first). */
    release(finalizer?: Finalizer): void {
        this.refs = Math.max(0, this.refs - 1);
        if (finalizer) this.finalizers.add(finalizer);
        if (this.refs > 0) return;
        if (this.stopTimer !== undefined) window.clearTimeout(this.stopTimer);
        this.stopTimer = window.setTimeout(() => void this.shutdown(), this.opts.graceMs);
    }

    /** Add work for the final release of the current holders (see `release`). */
    addFinalizer(finalizer: Finalizer): void {
        this.finalizers.add(finalizer);
    }

    /** The token just became available (or changed) — connect if we were waiting for it. */
    tokenReady(): void {
        if (this.refs > 0) void this.ensureStarted();
    }

    /** Throw the connection away and start a fresh one (the "Rejoin" button). */
    async restart(): Promise<void> {
        const old = this.connection;
        this.connection = null;
        if (this.retryTimer !== undefined) window.clearTimeout(this.retryTimer);
        this.retryTimer = undefined;
        this.attempt = 0;
        if (old) await old.stop().catch(() => undefined);
        if (this.refs > 0) {
            this.build();
            await this.ensureStarted();
        }
    }

    on(name: string, handler: Handler): () => void {
        let set = this.handlers.get(name);
        if (!set) {
            set = new Set();
            this.handlers.set(name, set);
        }
        set.add(handler);
        if (this.connection) this.attach(this.connection, name);
        return () => { this.handlers.get(name)?.delete(handler); };
    }

    subscribe(listener: () => void): () => void {
        this.stateListeners.add(listener);
        return () => { this.stateListeners.delete(listener); };
    }

    async invoke(name: string, ...args: unknown[]): Promise<void> {
        const c = this.connection;
        if (!c || c.state !== HubConnectionState.Connected) return;
        try { await c.invoke(name, ...args); } catch { /* the SignalR logger notes it */ }
    }

    async invokeResult<T>(name: string, ...args: unknown[]): Promise<T | undefined> {
        const c = this.connection;
        if (!c || c.state !== HubConnectionState.Connected) return undefined;
        try { return await c.invoke<T>(name, ...args); } catch { return undefined; }
    }

    /**
     * Like invoke, but falls back to `legacy` when the server doesn't have `name` yet (a new web bundle
     * talking to an older BFF during a deploy).
     */
    async invokeWithFallback(name: string, args: unknown[], legacy: string, legacyArgs: unknown[]): Promise<void> {
        const c = this.connection;
        if (!c || c.state !== HubConnectionState.Connected) return;
        try {
            await c.invoke(name, ...args);
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            if (/method does not exist|Failed to invoke/i.test(message)) {
                try { await c.invoke(legacy, ...legacyArgs); } catch { /* logged by SignalR */ }
            }
        }
    }

    private build(): void {
        const builder = new HubConnectionBuilder()
            .withUrl(this.opts.url, this.opts.token ? { accessTokenFactory: () => this.opts.token?.() ?? "" } : {})
            // The default policy gives up after ~42 s — a deploy or a laptop lid then left the page dead.
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (ctx) =>
                    ctx.previousRetryCount < RETRY_DELAYS.length ? RETRY_DELAYS[ctx.previousRetryCount]! : 30000
            })
            .configureLogging(LogLevel.Warning);
        if (this.opts.stateful) builder.withStatefulReconnect();
        const c = builder.build();
        // A call's signalling must not be declared dead because a background tab's timers were throttled.
        c.serverTimeoutInMilliseconds = 60_000;
        c.keepAliveIntervalInMilliseconds = 15_000;
        this.connection = c;
        for (const name of this.handlers.keys()) this.attach(c, name);
        const sync = () => { if (c === this.connection) this.setSnapshot(c); };
        c.onreconnecting(sync);
        c.onreconnected(sync);
        c.onclose(() => {
            if (c !== this.connection) return;
            this.setSnapshot(c);
            // Closed for good (e.g. the server refused a reconnect): start over while anyone still needs it.
            if (this.refs > 0) this.scheduleRetry();
        });
    }

    private attach(c: HubConnection, name: string): void {
        let names = this.attached.get(c);
        if (!names) {
            names = new Set();
            this.attached.set(c, names);
        }
        if (names.has(name)) return;
        names.add(name);
        c.on(name, (...args: unknown[]) => {
            const set = this.handlers.get(name);
            if (!set) return;
            for (const h of Array.from(set)) {
                try { h(...args); } catch (err) { console.error(`Hub handler for ${name} failed`, err); }
            }
        });
    }

    private async ensureStarted(): Promise<void> {
        const c = this.connection;
        if (!c || this.starting || c.state !== HubConnectionState.Disconnected) return;
        if (this.opts.token && !this.opts.token()) return;
        this.starting = true;
        this.snapshot = { state: HubConnectionState.Connecting, connectionId: null };
        this.emit();
        try {
            // Automatic reconnect does not cover a failed FIRST connect (joining while the server restarts),
            // hence the retry loop.
            await c.start();
            this.attempt = 0;
        } catch {
            if (c === this.connection && this.refs > 0) this.scheduleRetry();
        } finally {
            this.starting = false;
            if (c === this.connection) this.setSnapshot(c);
        }
    }

    private scheduleRetry(): void {
        if (this.retryTimer !== undefined) return;
        const delay = Math.min(30000, 1000 * 2 ** this.attempt);
        this.attempt += 1;
        this.retryTimer = window.setTimeout(() => {
            this.retryTimer = undefined;
            void this.ensureStarted();
        }, delay);
    }

    private async shutdown(): Promise<void> {
        this.stopTimer = undefined;
        if (this.refs > 0) return;
        const c = this.connection;
        const work = Array.from(this.finalizers);
        this.finalizers.clear();
        if (c && c.state === HubConnectionState.Connected) {
            for (const f of work) {
                try { await f(c); } catch { /* best effort */ }
            }
        }
        if (this.refs > 0) return;
        this.connection = null;
        if (this.retryTimer !== undefined) window.clearTimeout(this.retryTimer);
        this.retryTimer = undefined;
        this.attempt = 0;
        if (c) await c.stop().catch(() => undefined);
        this.snapshot = { state: HubConnectionState.Disconnected, connectionId: null };
        this.emit();
    }

    private setSnapshot(c: HubConnection): void {
        const next = { state: c.state, connectionId: c.connectionId ?? null };
        if (next.state === this.snapshot.state && next.connectionId === this.snapshot.connectionId) return;
        this.snapshot = next;
        this.emit();
    }

    private emit(): void {
        for (const l of Array.from(this.stateListeners)) l();
    }
}

// ── Access token for the hubs ────────────────────────────────────────────────
// Written by the hub hooks from react-oidc-context; read by the factories at negotiate time.
let hubAccessToken: string | undefined;
const tokenWaiters = new Set<SharedHub>();

export function setHubAccessToken(token: string | undefined): void {
    if (token === hubAccessToken) return;
    hubAccessToken = token;
    if (token) for (const hub of tokenWaiters) hub.tokenReady();
}

export function authedHub(url: string, extra: Omit<SharedHubOptions, "url" | "token">): SharedHub {
    const hub = new SharedHub({ url, token: () => hubAccessToken, ...extra });
    tokenWaiters.add(hub);
    return hub;
}
